import { useState, useRef, useEffect } from 'react';
import { Input } from './ui/input';

const CITIES = [
  'Atlanta, GA', 'Atlanta, GA 30301', 'Atlanta, GA 30303', 'Atlanta, GA 30309',
  'Washington, DC', 'Washington, DC 20001', 'Washington, DC 20009',
  'Houston, TX', 'Houston, TX 77001', 'Houston, TX 77004',
  'New Orleans, LA', 'New Orleans, LA 70112',
  'Baltimore, MD', 'Baltimore, MD 21201',
  'Nashville, TN', 'Nashville, TN 37201',
  'Charlotte, NC', 'Charlotte, NC 28201',
  'Raleigh, NC', 'Raleigh, NC 27601',
  'Durham, NC', 'Durham, NC 27701',
  'Richmond, VA', 'Norfolk, VA', 'Hampton, VA',
  'Birmingham, AL', 'Birmingham, AL 35201',
  'Montgomery, AL', 'Montgomery, AL 36101',
  'Jackson, MS', 'Jackson, MS 39201',
  'Memphis, TN', 'Memphis, TN 38101',
  'Baton Rouge, LA', 'Baton Rouge, LA 70801',
  'Tallahassee, FL', 'Tallahassee, FL 32301',
  'Greensboro, NC', 'Winston-Salem, NC',
  'Tuskegee, AL', 'Daytona Beach, FL',
  'Orangeburg, SC', 'Columbia, SC',
  'Savannah, GA', 'Albany, GA', 'Fort Valley, GA',
  'Pine Bluff, AR', 'Little Rock, AR',
  'St. Louis, MO', 'St. Louis, MO 63101',
  'Detroit, MI', 'Detroit, MI 48201',
  'Chicago, IL', 'Chicago, IL 60601',
  'Philadelphia, PA', 'Philadelphia, PA 19101',
  'New York, NY', 'Brooklyn, NY', 'Harlem, NY',
  'Boston, MA', 'Boston, MA 02101',
  'Miami, FL', 'Miami, FL 33101',
  'Tampa, FL', 'Orlando, FL', 'Jacksonville, FL',
  'Dallas, TX', 'Dallas, TX 75201',
  'Austin, TX', 'San Antonio, TX',
  'Los Angeles, CA', 'Los Angeles, CA 90001',
  'San Francisco, CA', 'Oakland, CA',
  'Seattle, WA', 'Denver, CO', 'Phoenix, AZ',
  'Las Vegas, NV', 'Minneapolis, MN',
  'Kansas City, MO', 'Indianapolis, IN',
  'Columbus, OH', 'Cleveland, OH', 'Cincinnati, OH',
  'Milwaukee, WI', 'Portland, OR',
  'San Diego, CA', 'Sacramento, CA',
  'Newark, NJ', 'Trenton, NJ',
  'Hampton Roads, VA', 'Virginia Beach, VA',
  'Wilmington, DE', 'Pittsburgh, PA',
  'Louisville, KY', 'Lexington, KY',
];

const LocationAutocomplete = ({ value, onChange, onSelect, placeholder = 'Enter a city...', className }) => {
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const containerRef = useRef(null);
  const listRef = useRef(null);

  const query = (value || '').trim().toLowerCase();
  const suggestions = query.length > 0
    ? CITIES.filter(city => city.toLowerCase().includes(query)).slice(0, 10)
    : [];

  // Close dropdown when clicking outside
  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Reset active index when suggestions change
  useEffect(() => {
    setActiveIndex(-1);
  }, [query]);

  // Scroll active item into view
  useEffect(() => {
    if (activeIndex >= 0 && listRef.current) {
      const items = listRef.current.children;
      if (items[activeIndex]) {
        items[activeIndex].scrollIntoView({ block: 'nearest' });
      }
    }
  }, [activeIndex]);

  const handleSelect = (city) => {
    onChange(city);
    setOpen(false);
    if (onSelect) onSelect(city);
  };

  const handleKeyDown = (e) => {
    if (!open || suggestions.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex(prev => (prev < suggestions.length - 1 ? prev + 1 : 0));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex(prev => (prev > 0 ? prev - 1 : suggestions.length - 1));
    } else if (e.key === 'Enter' && activeIndex >= 0) {
      e.preventDefault();
      handleSelect(suggestions[activeIndex]);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  const highlightMatch = (text) => {
    if (!query) return text;
    const idx = text.toLowerCase().indexOf(query);
    if (idx === -1) return text;
    return (
      <>
        {text.slice(0, idx)}
        <span className="font-semibold text-dark-slate">{text.slice(idx, idx + query.length)}</span>
        {text.slice(idx + query.length)}
      </>
    );
  };

  return (
    <div ref={containerRef} className="relative">
      <Input
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
          setOpen(true);
        }}
        onFocus={() => { if (query.length > 0) setOpen(true); }}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        className={className}
        autoComplete="off"
      />
      {open && suggestions.length > 0 && (
        <ul
          ref={listRef}
          className="absolute z-50 left-0 right-0 mt-1 bg-white border border-slate-200 rounded-xl shadow-lg max-h-48 overflow-y-auto"
        >
          {suggestions.map((city, i) => (
            <li
              key={city}
              onMouseDown={() => handleSelect(city)}
              onMouseEnter={() => setActiveIndex(i)}
              className={`px-3 py-2 text-sm cursor-pointer transition-colors ${
                i === activeIndex ? 'bg-slate-100 text-dark-slate' : 'text-slate-gray hover:bg-slate-50'
              }`}
            >
              {highlightMatch(city)}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default LocationAutocomplete;
